import { SecurityService as security } from '../../common/services';
import { MLModelTypesResult, MLModelsResult, MLModel, MLModelType, MLModelUsage } from '../types';
import appClient from '../../common/services/AppClient';
import { Utils } from '../../common/services/Utils';
import { endpoints } from '../../../config';

export default class MLService {
    async getAllMLModels(): Promise<MLModel[]> {
        throw new Error('Global ML model reads are not supported by ManageService. Provide a project id.');
    }

    async getMLModelsForProject(projectId: string): Promise<MLModel[]> {
        const result = await appClient.get<MLModelsResult['getMLModels']>(
            `${endpoints.manageUrl}projects/${projectId}/ml_models`
        );
        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        return result.value || [];
    }

    async getMLModelTypes(projectId?: string): Promise<MLModelType[]> {
        if (!projectId) {
            throw new Error('ML model type reads require a project id.');
        }

        const result = await appClient.get<MLModelTypesResult['getMLModelTypes']>(
            `${endpoints.manageUrl}projects/${projectId}/ml_models/types`
        );
        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        return result.value || [];
    }

    addMLModel(model: MLModel, revisionFilePath: string | undefined) {
        const formData = new FormData();
        formData.append('type', model.type);
        formData.append('name', model.name);
        formData.append('projectId', model.projectId);
        formData.append('labels', JSON.stringify(model.labels));
        formData.append('device', model.device);
        if (revisionFilePath) {
            // @ts-ignore
            formData.append('revisionFilePath', revisionFilePath);
        }

        const url = endpoints.manageUrl + `projects/${model.projectId}/ml_models`;

        return appClient.post(url, formData);
    }

    updateMLModel(model: MLModel) {
        const url = endpoints.manageUrl + `projects/${model.projectId}/ml_models/${model.id}`;

        return appClient.update(url, model);
    }

    updateRevisionState(model: MLModel, storagePath: string, isActive: boolean) {
        const request = {
            modelId: model.id!,
            storagePath,
            isActive: isActive
        };
        const url = endpoints.manageUrl + `projects/${model.projectId}/ml_models/${model.id}/change_state`;
        return appClient.post(url, request);
    }

    uploadRevision(formData: FormData, model: MLModel) {
        const url = endpoints.manageUrl + `projects/${model.projectId}/ml_models/${model.id}/upload`;
        return appClient.post(url, formData);
    }

    deleteRevision(model: MLModel, storagePath: string) {
        const url =
            endpoints.manageUrl +
            `projects/${model.projectId}/ml_models/${model.id}/${encodeURIComponent(storagePath)}`;
        return appClient.delete(url);
    }

    deleteMLModel(model: MLModel) {
        const url = endpoints.manageUrl + `projects/${model.projectId}/ml_models/${model.id}`;
        return appClient.delete(url);
    }

    async downloadRevision(model: MLModel, storagePath: string) {
        security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url =
                endpoints.manageUrl +
                `projects/${model.projectId}/ml_models/${model.id}/${encodeURIComponent(storagePath)}${t}`;
            window.open(url);
            return Promise.resolve();
        });
    }

    async importMlModel(data: FormData, projectId: string) {
        let url = endpoints.manageUrl + `projects/${projectId}/ml_models/import`;
        const resp = await appClient.post<unknown | undefined>(url, data);
        return resp;
    }

    async exportMlModel(projectId: string, modelId: string) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'mlModelPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/ml_models/${modelId}/export${t}`;
            Utils.downloadFile(url, mapForm, 'mlModelPostForm');
            return Promise.resolve();
        });
    }

    async getModelUsage(projectId: string, modelId: string) {
        let url = endpoints.manageUrl + `projects/${projectId}/ml_models/${modelId}/usage`;
        const resp = await appClient.get<MLModelUsage | undefined>(url);
        return resp;
    }

    async updateManifest(projectId: string, modelId: string, storagePath: string, manifest: string) {
        let url = endpoints.manageUrl + `projects/${projectId}/ml_models/${modelId}/manifest`;

        const request = {
            storagePath,
            manifest
        };

        const resp = await appClient.post<unknown | undefined>(url, request);
        return resp;
    }

    getAuthToken() {
        return security.invoke(token => {
            return Promise.resolve(token);
        });
    }
}
